import { useState } from 'react';
import { SEOMeta } from '../components/common/SEOMeta';
import { SectionTitle } from '../components/common/SectionTitle';
import { useScrollReveal } from '../hooks/useScrollReveal';
import '../assets/css/pages/Portfolio.css';

type Category = 'All' | 'Web' | 'Mobile' | 'Design' | 'ERP' | 'UI/UX';

interface Project {
  id: number;
  title: string;
  category: Category;
  icon: string;
  desc: string;
  tech: string[];
  stripColor?: string;
}

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState<Category>('All');
  const headerReveal = useScrollReveal();
  const gridReveal = useScrollReveal();

  const projects: Project[] = [
  { id: 1, title: "FinServe Analytics Platform", category: "Web", icon: "chart-pie", desc: "A comprehensive financial dashboard processing real-time market data with advanced charting capabilities.", tech: ["React", "TypeScript", "Node.js", "PostgreSQL"], stripColor: "linear-gradient(135deg, #0f2027, #0f3460)" },
    { id: 2, title: "HealthTrack Patient Portal", category: "Mobile", icon: "heart-pulse", desc: "Secure mobile application for patients to access medical records, schedule appointments, and consult doctors via video.", tech: ["React Native", "Firebase", "WebRTC"], stripColor: "linear-gradient(135deg, #0f2027, #2c5364)" },
    { id: 3, title: "Nexus E-Commerce Architecture", category: "Web", icon: "cart-shopping", desc: "A highly scalable headless commerce solution handling 10k+ concurrent users during peak flash sales.", tech: ["Next.js", "GraphQL", "AWS"], stripColor: "linear-gradient(135deg, #0f2027, #0f3460)" },
    { id: 4, title: "LogisFleet Management System", category: "ERP", icon: "truck-fast", desc: "Enterprise web application for tracking logistics, optimizing routes, and managing fleet maintenance schedules.", tech: ["Vue.js", "Python", "Django"], stripColor: "linear-gradient(135deg, #1a0a0a, #4a2020)" },
    { id: 5, title: "Aura Smart Home App", category: "Mobile", icon: "house-signal", desc: "IoT companion application controlling smart home devices with predictive automation algorithms.", tech: ["Flutter", "IoT Core", "AWS"], stripColor: "linear-gradient(135deg, #0f2027, #2c5364)" },
    { id: 6, title: "Vanguard Security UI", category: "UI/UX", icon: "shield-halved", desc: "Complete UX/UI overhaul for a cybersecurity monitoring tool, reducing alert response time by 40%.", tech: ["Figma", "Design System"], stripColor: "linear-gradient(135deg, #1f0533, #5a1091)" },
    { id: 7, title: "EduSphere Learning LMS", category: "Web", icon: "graduation-cap", desc: "Modern learning management system featuring live classes, automated grading, and student analytics.", tech: ["React", "Express", "MongoDB"], stripColor: "linear-gradient(135deg, #0f2027, #0f3460)" },
    { id: 8, title: "NeoBank Mobile Wallet", category: "Mobile", icon: "wallet", desc: "Next-generation mobile banking application featuring instant transfers, crypto integration, and AI spending insights.", tech: ["React Native", "Go", "K8s"], stripColor: "linear-gradient(135deg, #0f2027, #2c5364)" },
    { id: 9, title: "OmniChannel Retail ERP", category: "ERP", icon: "boxes-stacked", desc: "Unified ERP connecting inventory, sales, and supply chain across 50+ retail locations.", tech: ["Angular", "Java", "Oracle"], stripColor: "linear-gradient(135deg, #1a0a0a, #4a2020)" },
    { id: 10, title: "Elevate Brand Identity", category: "Design", icon: "palette", desc: "Full digital brand identity and visual language system for a leading AI startup.", tech: ["Adobe CC", "Figma", "Motion"], stripColor: "linear-gradient(135deg, #1f0533, #3d0b5e)" }
  ];

  const filteredProjects = activeFilter === 'All' ? projects : projects.filter(p => p.category === activeFilter);
  const filters: Category[] = ['All', 'Web', 'Mobile', 'Design', 'ERP', 'UI/UX'];

  return (
    <>
      <SEOMeta
        title="Portfolio"
        description="Explore our recent projects and successful digital solutions delivered by Logicraft Infotech."
        canonical="https://www.logicraftinfotech.com/portfolio"
      />

      <section className="page-header" ref={headerReveal.ref}>
        <div className={`container ${headerReveal.className}`}>
          <SectionTitle title="Our Portfolio" centered />
          <p className="page-header-desc" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto', color: 'var(--text-secondary)', fontSize: '1.2rem' }}>
            A showcase of digital excellence. We build products that solve real problems, drive business value, and provide exceptional user experiences.
          </p>
        </div>
      </section>

      <section className="section" ref={gridReveal.ref}>
        <div className={`container ${gridReveal.className}`}>
          <div className="portfolio-filters">
            {filters.map(filter => (
              <button key={filter} className={`filter-btn ${activeFilter === filter ? 'active' : ''}`} onClick={() => setActiveFilter(filter)}>
                {filter}
              </button>
            ))}
          </div>

         <div className="portfolio-grid-new">
            {filteredProjects.map((proj) => (
              <div className="pf-card" key={proj.id}>
                <div className="pf-visual" style={{ background: proj.stripColor || 'linear-gradient(135deg, #161b22, #243b55)' }}>
                  <i className={`fa-solid fa-${proj.icon}`}></i>
                  <span className="pf-cat-badge">{proj.category}</span>
                </div>
                <div className="pf-body">
                  <h3 className="pf-title">{proj.title}</h3>
                  <p className="pf-desc">{proj.desc}</p>
                  <div className="pf-tech-row">
                    {proj.tech.map(t => <span key={t} className="pf-tech-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center" style={{ padding: 'var(--spacing-xxl) 0', color: 'var(--text-secondary)' }}>
              <i className="fa-solid fa-folder-open" style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)', opacity: 0.5 }}></i>
              <p>No projects found for this category.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}